export const NATIVE_PAYMENT_PATH = '/third-platform/work-wx/native-payment';
export const COMMON_LOGIN_JUMP_PATH = '/third-platform/work-wx/common-login-jump';
const SESSION_KEY = 'wecom-payment-session';
const STATE_PREFIX = 'wecom-payment-state:';
const BINDING_KEY = 'wecom-payment-binding';

export type WecomEmployeeBinding = {
  bindingTicket: string;
  browserVerifier: string;
  corpId: string;
  corpName: string;
  memberName: string;
  createdAt: number;
};

export type WecomEntryCorp = { corpId: string; name: string };
export type WecomPaymentAuth = {
  status: 'ready' | 'select' | 'redirecting' | 'binding' | 'error';
  binding?: WecomEmployeeBinding;
  corpId?: string;
  corps?: WecomEntryCorp[];
  error?: string;
};

export function isWecomPaymentEntry() {
  return (
    window.location.pathname === NATIVE_PAYMENT_PATH &&
    /wxwork/i.test(navigator.userAgent)
  );
}

/**
 * 企微收款「绑定 CRM 员工」H5 流程的公用跳转页（common-login-jump）。
 * 该页只负责把企微下发的 code/state 原样转发回 H5，**绝不需要 CRM 登录态**，
 * 因此必须像 /user/login 一样被全局登录守卫豁免，否则在企微 webview（无 CRM 会话）里会被踢到登录页。
 * 仅按 pathname 判定（不校验 UA），因为其职责是"转发"而非"鉴权"，在任何环境都应直接执行。
 */
export function isCommonLoginJumpEntry() {
  return window.location.pathname === COMMON_LOGIN_JUMP_PATH;
}

async function call<T>(
  action: string,
  data: Record<string, unknown> = {},
): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 20000);
  try {
    const response = await fetch(
      `/api/crm/admin/work-wx-native-payment-login/${action}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        signal: controller.signal,
      },
    );
    const result = await response.json();
    if (!response.ok || !result.success)
      throw new Error(result.errorMessage || '企微自动登录暂不可用');
    return result.data as T;
  } finally {
    window.clearTimeout(timer);
  }
}

async function digest(value: string) {
  const bytes = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(value),
  );
  return Array.from(new Uint8Array(bytes), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
}

export function clearWecomPaymentSession() {
  sessionStorage.removeItem(SESSION_KEY);
}

export async function beginWecomPaymentLogin(corpId: string): Promise<void> {
  if (!/^[a-zA-Z][a-zA-Z0-9_-]{1,63}$/.test(corpId))
    throw new Error('入口corpId必须使用企业微信CorpID，请更新聊天工具栏地址');
  clearWecomPaymentSession();
  sessionStorage.removeItem(BINDING_KEY);
  const verifier = Array.from(
    crypto.getRandomValues(new Uint8Array(32)),
    (byte) => byte.toString(16).padStart(2, '0'),
  ).join('');
  const result = await call<{ authorizationUrl: string; state: string }>(
    'authorize',
    {
      corpId,
      browserChallenge: await digest(verifier),
      url: `${window.location.origin}${NATIVE_PAYMENT_PATH}?corpId=${encodeURIComponent(corpId)}`,
    },
  );
  const authorization = new URL(result.authorizationUrl);
  if (
    authorization.origin !== 'https://open.weixin.qq.com' ||
    authorization.pathname !== '/connect/oauth2/authorize' ||
    !/^[a-f0-9]{64}$/.test(result.state) ||
    authorization.searchParams.get('state') !== result.state
  ) {
    throw new Error('企微授权地址无效');
  }
  sessionStorage.setItem(
    STATE_PREFIX + result.state,
    JSON.stringify({ verifier, corpId, createdAt: Date.now() }),
  );
  window.location.replace(result.authorizationUrl);
}

export async function bootstrapWecomPaymentLogin(): Promise<WecomPaymentAuth> {
  const query = new URLSearchParams(window.location.search);
  const corpId = query.get('corpId') || undefined;
  try {
    const code = query.get('code');
    const state = query.get('state');
    if (code || state) {
      // 不在地址栏、浏览器历史或后续签名请求中保留一次性授权码。
      window.history.replaceState(
        window.history.state,
        '',
        `${NATIVE_PAYMENT_PATH}${corpId ? `?corpId=${encodeURIComponent(corpId)}` : ''}`,
      );
      const key = STATE_PREFIX + state;
      const saved = sessionStorage.getItem(key);
      sessionStorage.removeItem(key);
      if (!code || !state || !saved)
        throw new Error('企微登录已失效，请重新打开收款页面');
      const pending = JSON.parse(saved) as {
        verifier: string;
        corpId: string;
        createdAt: number;
      };
      if (pending.corpId !== corpId || Date.now() - pending.createdAt > 300000)
        throw new Error('企微登录已过期，请重试');
      const result = await call<{
        token?: string;
        corpId: string;
        bindingRequired?: boolean;
        bindingTicket?: string;
        corpName?: string;
        memberName?: string;
      }>('complete', {
        code,
        state,
        browserVerifier: pending.verifier,
      });
      if (result.corpId !== corpId) throw new Error('企微登录返回身份不一致');
      if (result.bindingRequired && result.bindingTicket) {
        const binding: WecomEmployeeBinding = {
          bindingTicket: result.bindingTicket,
          browserVerifier: pending.verifier,
          corpId: result.corpId,
          corpName: result.corpName || '',
          memberName: result.memberName || '',
          createdAt: Date.now(),
        };
        sessionStorage.setItem(BINDING_KEY, JSON.stringify(binding));
        clearWecomPaymentSession();
        return { status: 'binding', corpId, binding };
      }
      if (!result.token) throw new Error('企微登录返回身份不一致');
      await saveWecomPaymentSession(result.token, result.corpId);
      return { status: 'ready', corpId };
    }
    const pendingBinding = sessionStorage.getItem(BINDING_KEY);
    if (pendingBinding) {
      const binding = JSON.parse(pendingBinding) as WecomEmployeeBinding;
      if (
        binding.corpId === corpId &&
        Date.now() - binding.createdAt < 600000
      ) {
        return { status: 'binding', corpId, binding };
      }
      sessionStorage.removeItem(BINDING_KEY);
    }
    const token = localStorage.getItem('token');
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (corpId && token && saved) {
      const session = JSON.parse(saved) as {
        corpId: string;
        tokenHash: string;
        createdAt: number;
      };
      if (
        session.corpId === corpId &&
        session.tokenHash === (await digest(token)) &&
        Date.now() - session.createdAt < 28800000
      ) {
        return { status: 'ready', corpId };
      }
    }
    if (corpId) {
      await beginWecomPaymentLogin(corpId);
      return { status: 'redirecting', corpId };
    }
    const options = await call<{ corps: WecomEntryCorp[] }>('options');
    if (options.corps.length === 1) {
      await beginWecomPaymentLogin(options.corps[0].corpId);
      return { status: 'redirecting', corpId: options.corps[0].corpId };
    }
    if (!options.corps.length)
      throw new Error('尚未配置可用的商品收款企业，请联系管理员');
    return { status: 'select', corps: options.corps };
  } catch (reason) {
    clearWecomPaymentSession();
    return {
      status: 'error',
      corpId,
      error:
        reason instanceof Error
          ? reason.message
          : '企微自动登录失败，请重新打开',
    };
  }
}

async function saveWecomPaymentSession(token: string, corpId: string) {
  localStorage.setItem('token', token);
  sessionStorage.setItem(
    SESSION_KEY,
    JSON.stringify({
      corpId,
      tokenHash: await digest(token),
      createdAt: Date.now(),
    }),
  );
  sessionStorage.removeItem(BINDING_KEY);
}

export function wecomBindingEmployees(
  binding: WecomEmployeeBinding,
  keyword: string,
  current = 1,
) {
  return call<{
    employees: { id: string; name: string }[];
    total: number;
    current: number;
    pageSize: number;
  }>('employees', {
    bindingTicket: binding.bindingTicket,
    browserVerifier: binding.browserVerifier,
    keyword,
    current,
  });
}

export async function bindWecomPaymentEmployee(
  binding: WecomEmployeeBinding,
  employeeId: string,
) {
  const result = await call<{ token: string; corpId: string }>(
    'bind-employee',
    {
      bindingTicket: binding.bindingTicket,
      browserVerifier: binding.browserVerifier,
      employeeId,
    },
  );
  if (!result.token || result.corpId !== binding.corpId)
    throw new Error('绑定返回身份不一致，请重新打开');
  await saveWecomPaymentSession(result.token, result.corpId);
}
